// src/components/Profile/UserCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  Box,
  IconButton,
  Chip,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  PersonAdd,
  PersonRemove,
  Message,
  MoreVert,
  Verified,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { toggleFollow } from '../../services/api';
import S3Avatar from '../Common/S3Avatar';

const UserCard = ({ user, variant = 'card', onClose, showFollowButton = true, onFollowToggle, currentUserFollowingList = [] }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser, refreshUser } = useAuth();
  const { addNotification } = useApp();
  const [following, setFollowing] = useState(false); // Initialize to false, will be updated by useEffect

  useEffect(() => {
    // Use currentUserFollowingList to determine follow status
    const isCurrentlyFollowing = currentUserFollowingList.includes(user?.id);
    setFollowing(isCurrentlyFollowing);
  }, [user?.id, currentUserFollowingList]);

  const [loading, setLoading] = useState(false);

  const isOwnProfile = user?.id === currentUser?.id;

  const handleFollow = async (e) => {
    e.stopPropagation();
    
    if (!currentUser) {
      addNotification({
        type: 'warning',
        message: 'Please login to follow users',
      });
      return;
    }

    setLoading(true);
    try {
      await toggleFollow(user.id);
      setFollowing(!following);
      onFollowToggle?.(user.id, !following); // Call the callback
      
      // Refresh user data to update follower counts in sidebar/profile
      refreshUser();
      
      addNotification({
        type: 'success',
        message: following ? `Unfollowed ${user.displayName}` : `Following ${user.displayName}`,
      });
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to update follow status',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = () => {
    navigate(`/profile/${user.username}`);
    onClose?.();
  };

  const handleMessage = (e) => {
    e.stopPropagation();
    // Implement messaging functionality
    addNotification({
      type: 'info',
      message: 'Messaging feature coming soon!',
    });
  };

  if (variant === 'list') {
    return (
      <ListItemButton
        onClick={handleProfileClick}
        sx={{
          borderRadius: 3,
          mb: 1,
          py: 2,
          minHeight: 76,
          alignItems: 'center',
          transition: 'all 0.3s ease',
          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.15)',
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.mode === 'dark' ? '0 12px 40px rgba(0, 0, 0, 0.6)' : '0 12px 40px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 68 }}>
          <S3Avatar 
            s3Key={user.avatar}
            sx={{ 
              width: 52, 
              height: 52,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {user.displayName?.[0]?.toUpperCase()}
          </S3Avatar>
        </ListItemAvatar>
        
        <ListItemText
          sx={{ flex: 1 }}
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography 
                variant="subtitle1" 
                fontWeight="medium"
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '220px',
                  fontWeight: 600
                }}
              >
                {user.displayName}
              </Typography>
              {user.verified && (
                <Verified color="primary" sx={{ fontSize: 16 }} />
              )}
            </Box>
          }
          secondary={
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {user.bio && (
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: 'rgba(0, 0, 0, 0.6)', 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '200px'
                }}>
                  {user.bio.length > 40 ? `${user.bio.substring(0, 40)}...` : user.bio}
                </span>
              )}
              <span style={{ 
                fontSize: '0.75rem', 
                color: 'rgba(0, 0, 0, 0.6)', 
                whiteSpace: 'nowrap', 
                opacity: 0.8 
              }}>
                {user.followersCount || 0} followers
              </span>
            </span>
          }
        />

        {showFollowButton && !isOwnProfile && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant={following ? "outlined" : "contained"}
              size="small"
              onClick={handleFollow}
              disabled={loading}
              sx={{ 
                minWidth: 80, 
                fontSize: '0.75rem',
                background: following ? 'rgba(255, 255, 255, 0.1)' : (theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.8)' : 'rgba(255, 193, 7, 0.8)'),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 193, 7, 0.5)'}`,
                '&:hover': {
                  background: following ? 'rgba(255, 255, 255, 0.2)' : (theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.9)' : 'rgba(255, 193, 7, 0.9)'),
                }
              }}
            >
              {following ? 'Unfollow' : 'Follow'}
            </Button>
            <IconButton
              size="small"
              onClick={handleMessage}
              sx={{ 
                background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.4)' : 'rgba(255, 193, 7, 0.4)'}`,
                '&:hover': {
                  background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <Message fontSize="small" />
            </IconButton>
          </Box>
        )}

      </ListItemButton>
    );
  }

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
      onClick={handleProfileClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="flex-start" gap={2}>
          <S3Avatar 
            s3Key={user.avatar}
            sx={{ width: 56, height: 56 }}
          >
            {user.displayName?.[0]?.toUpperCase()}
          </S3Avatar>
          
          <Box flexGrow={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h6" component="div" noWrap>
                {user.displayName}
              </Typography>
              {user.verified && (
                <Verified color="primary" sx={{ fontSize: 18 }} />
              )}
            </Box>
            


            {user.bio && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {user.bio}
              </Typography>
            )}



            {/* Interests */}
            {user.interests && user.interests.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                {user.interests.slice(0, 3).map((interest, index) => (
                  <Chip
                    key={index}
                    label={interest}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
                {user.interests.length > 3 && (
                  <Chip
                    label={`+${user.interests.length - 3}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            )}

            {/* Location */}
            {user.location && (
              <Typography variant="caption" color="text.secondary" display="block">
                📍 {user.location}
              </Typography>
            )}
          </Box>


        </Box>
      </CardContent>
    </Card>
  );
};

export default UserCard;
